import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

// One cohesive mood axis driven by the iPhone Focus state.
// The state is invisible to visitors — it shifts the whole site's palette.
// Gradients live here (painted by FocusBackground, slowly drifting via the
// background-position animation in global.css); the matching flat color
// tokens live in global.css as html[data-focus="…"] variable sets — each
// gradient's 55% stop must stay identical to that theme's `--bg`.
export const FOCUS_THEMES = {
  Dawn: {
    // Pure, uncompromising black for heavy focus
    gradient: "linear-gradient(165deg, #000000 0%, #070707 55%, #121212 100%)",
  },
  Morning: {
    // Dark gray — charcoal easing out of the black
    gradient: "linear-gradient(165deg, #1d1f23 0%, #26282c 55%, #34373d 100%)",
  },
  Noon: {
    // Light brown — warm sand at the day's peak
    gradient: "linear-gradient(165deg, #e6d7bf 0%, #d9c7ac 55%, #c2ab89 100%)",
  },
  Dusk: {
    // White-ish — bright paper before the light drops
    gradient: "linear-gradient(165deg, #fbfaf7 0%, #f4f2ee 55%, #e7e1d6 100%)",
  },
  Night: {
    // Brownest phase for winding down
    gradient: "linear-gradient(165deg, #4a3a2d 0%, #3a2c22 55%, #241a12 100%)",
  },
  Sleep: {
    // Brown gradient; .focus-layer--sleep::after breathes black over it
    gradient: "linear-gradient(165deg, #231a12 0%, #17100b 55%, #0a0705 100%)",
  },
  None: {
    // Phone offline / nothing reported — near-black base
    gradient: "linear-gradient(165deg, #181818 0%, #0d0d0d 55%, #050505 100%)",
  },
};

// The local dev server has no API — always talk to the deployed endpoint.
// Absolute (not "/api/focus") so `npm start` works too, and the www. form because
// the bare apex 308-redirects to it. Overridable for preview/staging hosts.
const FOCUS_API =
  process.env.REACT_APP_FOCUS_API || "https://www.oliverbujdos.dev/api/focus";

// The Shortcut can report variants like "Noon'" — strip trailing apostrophes
// and map anything unknown to None.
export function normalizeFocus(raw) {
  if (typeof raw !== "string") return "None";
  const cleaned = raw.trim().replace(/['’]+$/, "");
  return FOCUS_THEMES[cleaned] ? cleaned : "None";
}

const FocusThemeContext = createContext({ focus: "None", ready: false });

export function FocusThemeProvider({ children }) {
  const [focus, setFocus] = useState("None");
  // flips once the first fetch settles (or immediately when pinned) —
  // the loading screen holds until then so the reveal is already themed
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // ?focus=Dusk pins a state for previewing transitions without the phone
    const pinned = new URLSearchParams(window.location.search).get("focus");
    if (pinned) {
      setFocus(normalizeFocus(pinned));
      setReady(true);
      return;
    }

    const fetchFocus = async () => {
      try {
        const response = await fetch(FOCUS_API);
        const data = await response.json();
        setFocus(normalizeFocus(data.focus));
      } catch (error) {
        console.error("Error fetching focus:", error);
      } finally {
        setReady(true);
      }
    };

    fetchFocus();
    const interval = setInterval(fetchFocus, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.focus = focus;
    // keep the browser chrome (mobile address bar) on the same mood
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute(
        "content",
        getComputedStyle(document.documentElement)
          .getPropertyValue("--bg")
          .trim() || "#0d0d0d"
      );
    }
  }, [focus]);

  return (
    <FocusThemeContext.Provider value={{ focus, ready }}>
      {children}
    </FocusThemeContext.Provider>
  );
}

export function useFocusTheme() {
  return useContext(FocusThemeContext);
}
